import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Clock, FileText, LifeBuoy, Mail, MessageCircle, Phone, Send, Store } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FAQ, POLICIES, UPDATED, fill } from "@/lib/policies";
import { siteUrl } from "@/lib/sites";

// Help centre, Contact us, policy pages and seller support (CUS-2).
let cached = null;
export function useSiteInfo() {
  const [info, setInfo] = useState(cached);
  useEffect(() => {
    if (cached) return;
    api.get("/site-info").then(({ data }) => { cached = data; setInfo(data); }).catch(() => setInfo({}));
  }, []);
  return info;
}

const POLICY_LINKS = [["terms", "Terms of service"], ["privacy", "Privacy policy"], ["cookies", "Cookie policy"], ["refunds", "Refund and return policy"],
  ["shipping", "Shipping and delivery"], ["sellers", "Seller policy"]];

function Shell({ title, intro, children, side }) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{title}</h1>
      {intro && <p className="text-stone-600 mt-2 max-w-3xl">{intro}</p>}
      <div className="mt-8 grid lg:grid-cols-[1fr_17rem] gap-8 items-start">
        <div className="min-w-0">{children}</div>
        <aside className="space-y-4 lg:sticky lg:top-24">{side}</aside>
      </div>
    </div>
  );
}

function PolicyNav({ current }) {
  return (
    <nav className="rounded-2xl border border-stone-200 bg-white p-4 text-sm" aria-label="Policies" data-testid="policy-nav">
      <div className="font-semibold mb-2">Policies</div>
      <ul className="space-y-1.5">
        {POLICY_LINKS.map(([k, t]) => (
          <li key={k}><Link to={`/policies/${k}`} className={k === current ? "text-marigold font-semibold" : "text-stone-600 hover:text-marigold"}>{t}</Link></li>
        ))}
        <li className="pt-1 border-t border-stone-100"><Link to="/help-centre" className="text-stone-600 hover:text-marigold">Help centre</Link></li>
        <li><Link to="/contact" className="text-stone-600 hover:text-marigold">Contact us</Link></li>
      </ul>
    </nav>
  );
}

function ContactCard({ info }) {
  if (!info) return null;
  const wa = (info.whatsapp || "").replace(/\D/g, "");
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 text-sm space-y-2" data-testid="contact-card">
      <div className="font-semibold">Talk to us</div>
      {info.email && <a href={`mailto:${info.email}`} className="flex items-center gap-2 text-stone-700 hover:text-marigold"><Mail className="h-4 w-4" />{info.email}</a>}
      {info.phone && <a href={`tel:${info.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-stone-700 hover:text-marigold"><Phone className="h-4 w-4" />{info.phone}</a>}
      {wa && <a href={`https://wa.me/${wa.length === 10 ? `91${wa}` : wa}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-stone-700 hover:text-marigold"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
      {info.hours && <div className="flex items-center gap-2 text-stone-500"><Clock className="h-4 w-4" />{info.hours}</div>}
      <Link to="/contact" className="inline-flex mt-1 text-marigold font-semibold">Send us a message →</Link>
    </div>
  );
}

/** /policies/:slug */
export function PolicyPage() {
  const { slug } = useParams();
  const info = useSiteInfo();
  const p = POLICIES[slug];
  useEffect(() => { document.title = p ? `${p.title} · PawBazaar` : "PawBazaar"; window.scrollTo(0, 0); }, [p]);
  if (!p) return <Shell title="Page not found" side={<PolicyNav />}><p className="text-stone-600">Choose a policy from the list.</p></Shell>;
  return (
    <Shell title={p.title} intro={p.intro} side={<><PolicyNav current={slug} /><ContactCard info={info} /></>}>
      <article className="rounded-3xl border border-stone-200 bg-white p-6 sm:p-8 space-y-7" data-testid={`policy-${slug}`}>
        <p className="text-xs text-stone-500">Last updated {UPDATED}</p>
        {p.sections.map(([h, paras]) => (
          <section key={h}>
            <h2 className="font-heading text-xl font-semibold mb-2">{h}</h2>
            {paras.map((t, n) => <p key={n} className="text-stone-700 leading-relaxed mb-2 whitespace-pre-line">{fill(t, info)}</p>)}
          </section>
        ))}
      </article>
    </Shell>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-100 last:border-0">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 py-3 text-left font-medium" aria-expanded={open}>
        {q}<ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-3 text-stone-600 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}

/** /help-centre: FAQ, quick links and how to reach us. */
export function HelpCentre() {
  const info = useSiteInfo();
  const { user } = useAuth();
  useEffect(() => { document.title = "Help centre · PawBazaar"; }, []);
  return (
    <Shell title="Help centre" intro="Answers to common questions. Can't find yours? We're here to help." side={<><ContactCard info={info} /><PolicyNav /></>}>
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        <Link to={user ? "/account" : "/login"} className="rounded-2xl border border-stone-200 bg-white p-4 hover:border-marigold" data-testid="help-track">
          <FileText className="h-5 w-5 text-marigold" /><div className="font-semibold mt-2">Track an order</div><div className="text-xs text-stone-500">See where it is, cancel or get help</div></Link>
        <Link to={user ? "/account?tab=help" : "/login"} className="rounded-2xl border border-stone-200 bg-white p-4 hover:border-marigold" data-testid="help-report">
          <LifeBuoy className="h-5 w-5 text-marigold" /><div className="font-semibold mt-2">Report a problem</div><div className="text-xs text-stone-500">With an order, booking or payment</div></Link>
        <Link to="/contact" className="rounded-2xl border border-stone-200 bg-white p-4 hover:border-marigold" data-testid="help-contact">
          <Mail className="h-5 w-5 text-marigold" /><div className="font-semibold mt-2">Contact us</div><div className="text-xs text-stone-500">Anything else</div></Link>
      </div>
      <div className="space-y-6" data-testid="faq">
        {FAQ.map(([group, items]) => (
          <section key={group} className="rounded-3xl border border-stone-200 bg-white px-5 py-3">
            <h2 className="font-heading text-lg font-semibold pt-2">{group}</h2>
            {items.map(([q, a]) => <Faq key={q} q={q} a={a} />)}
          </section>
        ))}
      </div>
    </Shell>
  );
}

const TOPICS = [["order", "An order"], ["care", "A care booking"], ["community", "Adoption or an NGO"], ["payment", "Payment or refund"],
  ["account", "My account"], ["selling", "Selling on PawBazaar"], ["partnership", "Partnership or press"], ["other", "Something else"]];

/** /contact: a message to PawBazaar Support (lands in the Support Inbox). */
export function ContactPage() {
  const info = useSiteInfo();
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", email: "", phone: "", topic: "order", reference: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { document.title = "Contact us · PawBazaar"; }, []);
  useEffect(() => { if (user) setF((x) => ({ ...x, name: x.name || user.name || "", email: x.email || user.email || "", phone: x.phone || user.phone || "" })); }, [user]);
  const ok = f.name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(f.email.trim()) && f.message.trim().length >= 10;
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, phone: f.phone || null, reference: f.reference || null, website: f.website || null, site: "shop" });
      setSent(true);
    } catch (x) {
      toast.error(x.response?.status === 429 ? "You've sent a few messages already. Please wait a little and try again." : "Couldn't send just now. Please try again.");
    } finally { setBusy(false); }
  };
  const field = "w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-marigold";
  return (
    <Shell title="Contact us" intro="Write to us and our support team will reply by email, usually within a day." side={<><ContactCard info={info} /><PolicyNav /></>}>
      {sent ? (
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6" data-testid="contact-sent">
          <div className="font-heading text-xl font-semibold">Thanks, we've got your message</div>
          <p className="text-stone-700 mt-2">We'll reply to {f.email}. For an order problem you can also use Report a problem on the order for the quickest help.</p>
        </div>
      ) : (
        <form onSubmit={send} className="rounded-3xl border border-stone-200 bg-white p-6 space-y-4" data-testid="contact-form">
          {user && <p className="text-sm text-stone-600 rounded-xl bg-marigold/10 px-3 py-2">About an order or booking? <Link to="/account?tab=help" className="text-marigold font-semibold">Report a problem</Link> on it to get help faster.</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">Your name<input className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" /></label>
            <label className="text-sm">Email<input type="email" className={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" /></label>
            <label className="text-sm">Mobile (optional)<input className={field} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
            <label className="text-sm">It's about<select className={field} value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="contact-topic">
              {TOPICS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select></label>
          </div>
          <label className="text-sm block">Order or booking number (optional)<input className={field} value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="e.g. #A1B2C3D4" /></label>
          <label className="text-sm block">Message<textarea rows={6} className={field} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" placeholder="Tell us what happened and how we can help" /></label>
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="hidden" aria-hidden="true" />
          <button type="submit" disabled={!ok || busy} data-testid="contact-send"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-stoneWarm text-white text-sm font-semibold disabled:opacity-50"><Send className="h-4 w-4" />{busy ? "Sending…" : "Send message"}</button>
          <p className="text-xs text-stone-500">We use these details only to answer you. See our <Link to="/policies/privacy" className="underline">Privacy policy</Link>.</p>
        </form>
      )}
    </Shell>
  );
}

/** /sellers: help for shops, care providers and NGOs. */
export function SellerSupport() {
  const info = useSiteInfo();
  useEffect(() => { document.title = "Seller support · PawBazaar"; }, []);
  const steps = [
    ["Sign up", "Business details, documents and bank account in four steps. You can stop and carry on later."],
    ["Get verified", "Our team checks your documents, usually within 2 working days."],
    ["Go live", "Add products (or services), set prices, stock and hours, and start taking orders."],
    ["Get paid", "Online orders are settled to your bank; see Earnings for commission, payouts, invoices and GST."],
  ];
  return (
    <Shell title="Seller support" intro="For pet shops, care providers and NGOs on PawBazaar." side={<><ContactCard info={info} /><PolicyNav /></>}>
      <div className="flex flex-wrap gap-3 mb-6">
        <a href={`${siteUrl("console")}/join/shop`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-marigold text-white text-sm font-semibold" data-testid="sellers-join"><Store className="h-4 w-4" />Sell on PawBazaar</a>
        <a href={`${siteUrl("console")}/login`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm font-semibold">Partner sign-in</a>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {steps.map(([t, d], n) => (
          <div key={t} className="rounded-2xl border border-stone-200 bg-white p-4"><div className="text-xs text-marigold font-semibold">Step {n + 1}</div><div className="font-semibold">{t}</div><div className="text-sm text-stone-600 mt-1">{d}</div></div>
        ))}
      </div>
      <div className="rounded-3xl border border-stone-200 bg-white p-6 mt-6 text-sm text-stone-700 space-y-2">
        <h2 className="font-heading text-lg font-semibold">Already selling?</h2>
        <p>Sign in to the console and open <b>Support</b> in the menu and press <b>Report an issue</b> for orders, payouts, documents or anything else; it goes straight to our support team and you can follow the replies there.</p>
        <p>Read the <Link to="/policies/sellers" className="text-marigold font-semibold">Seller policy</Link> for the rules on listings, orders and payments.</p>
      </div>
    </Shell>
  );
}
